'use client'

import { AddToCartButton } from '@/AddToCartButton'
import { useCookieValue } from '@/Cookies/useCookies'
import { useLatestProductsQuery } from '@/generated/graphql'

type MasterclassOption = {
  id?: number | null
  price: number
  salePrice?: number | null
  currencySymbol?: string | null
}

interface MasterclassSubscribeButtonProps {
  className?: string
  options?: MasterclassOption[]
  productId: number
}

const MasterclassSubscribeButton = ({
  options = [],
  className = '',
  productId,
}: MasterclassSubscribeButtonProps) => {
  const brandCookie = useCookieValue('vi-brand')
  const brandCode =
    brandCookie === 'VANISHING_INC_UK'
      ? 'VANISHING_INC_UK'
      : 'VANISHING_INC_US'
  const currencyFallback = brandCode === 'VANISHING_INC_UK' ? '£' : '$'

  // Grab the specific masterclass product so we can reuse its pricing/options
  const { data: latestProductsData } = useLatestProductsQuery(
    {
      take: 1,
      skip: 0,
      brandCode: brandCode,
      maxBlurbLength: 0,
      where: {
        id: { eq: productId },
      },
      order: null,
    }
  )

  const fetchedOptions =
    latestProductsData?.ecommProducts?.items?.[0]?.options?.map(option => ({
      id: option.id ?? productId,
      price: option.price,
      salePrice: option.salePrice,
      currencySymbol: option.currency?.symbol,
    })) ?? []

  const addToCartOptions = fetchedOptions.map(option => ({
    id: option.id ?? productId,
    description: 'Vanishing Inc. Masterclass Subscription',
    price: option.price,
    salePrice: option.salePrice,
    currencySymbol: option.currencySymbol || currencyFallback,
    inStock: true,
    productType: '',
    needsShipping: false,
  }))

  const hasResolvedPrice = options.length > 0 || fetchedOptions.length > 0
  const firstOption = addToCartOptions[0]
  const price = hasResolvedPrice
    ? firstOption?.salePrice ??
      (typeof firstOption?.price === 'number' ? firstOption.price : null)
    : null
  const currencySymbol = firstOption?.currencySymbol || currencyFallback

  const buttonLabel = price
    ? `Join Masterclass Now (${currencySymbol}${price.toFixed(2)} / month)`
    : 'Join Masterclass Now'

  return (
    <AddToCartButton
      productId={productId}
      productName="Vanishing Inc. Masterclass Subscription"
      options={addToCartOptions}
      showOptions={false}
      showEmailNotification={false}
      className={`mt-8 rounded-md bg-emerald-600 px-6 py-3 text-base font-semibold shadow-md hover:bg-emerald-700 text-white ${className}`}
    >
      {buttonLabel}
    </AddToCartButton>
  )
}

export default MasterclassSubscribeButton
